<template>
  <div class="contact-page">
    <section class="contact-section">
      <div class="container">
        <div class="row align-items-start">
          <div class="col-lg-6 mb-4 mb-lg-0">
            <p class="eyebrow">Contact</p>
            <h1 class="hero-title">Let's talk about your venue.</h1>
            <p class="hero-sub">
              Drop your details and we'll set up a live walkthrough of the platform.
              Demos cover everything: bookings, payments, waivers, photos, surveys,
              and operations. Whatever matters most for how you run.
            </p>

            <div class="contact-info">
              <div class="contact-info-item">
                <svg-icon name="mail" :size="22" />
                <div>
                  <h4>Email us</h4>
                  <a href="mailto:contact@offthecouch.io">contact@offthecouch.io</a>
                </div>
              </div>

              <div class="contact-info-item">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" viewBox="0 0 16 16" class="discord-icon" aria-hidden="true">
                  <path d="M13.545 2.907a13.2 13.2 0 0 0-3.257-1.011.05.05 0 0 0-.052.025c-.141.25-.297.577-.406.833a12.2 12.2 0 0 0-3.658 0 8 8 0 0 0-.412-.833.05.05 0 0 0-.052-.025c-1.125.194-2.22.534-3.257 1.011a.04.04 0 0 0-.021.018C.356 6.024-.213 9.047.066 12.032q.003.022.021.037a13.3 13.3 0 0 0 3.995 2.02.05.05 0 0 0 .056-.019q.463-.63.818-1.329a.05.05 0 0 0-.01-.059l-.018-.011a9 9 0 0 1-1.248-.595.05.05 0 0 1-.02-.066l.015-.019q.127-.095.248-.195a.05.05 0 0 1 .051-.007c2.619 1.196 5.454 1.196 8.041 0a.05.05 0 0 1 .053.007q.121.1.248.195a.05.05 0 0 1-.004.085 8 8 0 0 1-1.249.594.05.05 0 0 0-.03.03.05.05 0 0 0 .003.041c.24.465.515.909.817 1.329a.05.05 0 0 0 .056.019 13.2 13.2 0 0 0 4.001-2.02.05.05 0 0 0 .021-.037c.334-3.451-.559-6.449-2.366-9.106a.03.03 0 0 0-.02-.019m-8.198 7.307c-.789 0-1.438-.724-1.438-1.612s.637-1.613 1.438-1.613c.807 0 1.45.73 1.438 1.613 0 .888-.637 1.612-1.438 1.612m5.316 0c-.788 0-1.438-.724-1.438-1.612s.637-1.613 1.438-1.613c.807 0 1.451.73 1.438 1.613 0 .888-.631 1.612-1.438 1.612"/>
                </svg>
                <div>
                  <h4>Join the community</h4>
                  <a href="https://discord.gg/752E9HM6tt" target="_blank" rel="noopener">Off The Couch on Discord</a>
                </div>
              </div>

              <div class="contact-info-item">
                <svg-icon name="file-text" :size="22" />
                <div>
                  <h4>Browse the docs</h4>
                  <a href="https://docs.offthecouch.io/" target="_blank" rel="noopener">docs.offthecouch.io</a>
                </div>
              </div>
            </div>
          </div>

          <div class="col-lg-5 ml-lg-auto">
            <div class="contact-form-card">
              <h3>Tell us about your venue</h3>
              <p class="form-helper">We typically respond within one business day.</p>

              <form @submit.prevent="submitDemoRequestForm" novalidate>
                <div class="row">
                  <div class="col-sm-6">
                    <label class="form-label" for="ct-first-name">First name<span class="form-required-mark">*</span></label>
                    <base-input
                      v-model="user.first_name"
                      id="ct-first-name"
                      placeholder="First name"
                      autocomplete="given-name"
                      :aria-invalid="fieldErrors.first_name ? 'true' : null"
                      :aria-describedby="fieldErrors.first_name ? 'ct-first-name-error' : null"
                    />
                    <span v-if="fieldErrors.first_name" id="ct-first-name-error" class="form-field-error">Required</span>
                  </div>
                  <div class="col-sm-6">
                    <label class="form-label" for="ct-last-name">Last name</label>
                    <base-input v-model="user.last_name" id="ct-last-name" placeholder="Last name" autocomplete="family-name" />
                  </div>
                </div>
                <label class="form-label" for="ct-email">Email<span class="form-required-mark">*</span></label>
                <base-input
                  v-model="user.email"
                  id="ct-email"
                  placeholder="you@venue.com"
                  type="email"
                  autocomplete="email"
                  :aria-invalid="fieldErrors.email ? 'true' : null"
                  :aria-describedby="fieldErrors.email ? 'ct-email-error' : null"
                />
                <span v-if="fieldErrors.email" id="ct-email-error" class="form-field-error">{{ fieldErrors.email }}</span>

                <label class="form-label" for="ct-phone">Phone</label>
                <base-input v-model="user.phone" id="ct-phone" placeholder="Optional" type="tel" autocomplete="tel" />

                <label class="form-label" for="ct-venue">Venue name</label>
                <base-input v-model="user.company_name" id="ct-venue" placeholder="Venue name" autocomplete="organization" />

                <div class="text-right">
                  <button type="submit" class="btn-cta-primary" :disabled="submitting">
                    {{ submitting ? 'Sending' : 'Send' }}
                  </button>
                </div>

                <p v-if="submitState === 'success'" class="form-status form-status-success" ref="successMsg">
                  <img src="/img/pandas/celebrate.png" alt="" class="form-status-panda" />
                  Thanks. We'll be in touch within one business day.
                </p>
                <p v-if="submitState === 'error'" class="form-status form-status-error">
                  Something went wrong sending that. Email us at contact@offthecouch.io and we'll sort it.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "ContactUs",
  data() {
    return {
      user: { first_name: '', last_name: '', email: '', company_name: '', phone: '' },
      fieldErrors: {},
      submitting: false,
      submitState: null,
    };
  },
  methods: {
    validateForm() {
      const errs = {};
      if (!this.user.first_name.trim()) errs.first_name = true;
      const email = this.user.email.trim();
      if (!email) errs.email = 'Required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Enter a valid email';
      this.fieldErrors = errs;
      return Object.keys(errs).length === 0;
    },
    async submitDemoRequestForm() {
      if (!this.validateForm()) return;
      this.submitting = true;
      this.submitState = null;
      try {
        await axios.post('https://api.offthecouch.io/demo-request', { user: this.user });
        this.submitState = 'success';
        this.user = { first_name: '', last_name: '', email: '', company_name: '', phone: '' };
        this.fieldErrors = {};
        this.$nextTick(() => {
          if (this.$refs.successMsg) {
            this.$refs.successMsg.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      } catch (err) {
        this.submitState = 'error';
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/marketing-tokens';

.contact-page { padding-top: 110px; padding-bottom: 80px; }

.contact-section {
  position: relative;
  // Clips the decorative blob below; without this its 200px overhang creates
  // horizontal scroll on narrow screens
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    top: -200px; right: -200px;
    width: 600px; height: 600px;
    background: radial-gradient(circle, rgba(225, 78, 202, 0.12), transparent 60%);
    z-index: 0;
    pointer-events: none;
  }
  .container { position: relative; z-index: 1; }
}

.hero-sub {
  color: $muted;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2.5rem;
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.contact-info-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: $card-bg;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;

  ::v-deep .svg-icon, .discord-icon {
    color: $teal;
    flex-shrink: 0;
    margin-top: 0.15rem;
  }

  h4 { color: white; font-size: 0.95rem; font-weight: 600; margin: 0 0 0.2rem; }
  a {
    color: $muted;
    font-size: 0.9rem;
    text-decoration: none;
    &:hover { color: $primary; text-decoration: none; }
  }
}

.contact-form-card {
  background: $card-bg;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 2rem;

  h3 {
    color: white;
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 0.4rem;
  }
}

.form-helper {
  color: $muted-2;
  font-size: 0.85rem;
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  color: $muted;
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: 0.4rem;
}

@media (max-width: 768px) {
  .contact-page { padding-top: 90px; }
  .contact-form-card { padding: 1.5rem; }
}
</style>
